import type { AppError } from '@aibi/shared';
import {
  Badge,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  EmptyState,
  Spinner,
} from '@aibi/ui';
import { BarChart3, Database, Pencil, PlugZap, Plus, Search, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useUiDispatch } from '../../workspace/state/ui-state';
import type { Connection, ConnectorCatalog } from '../services/admin-api';
import { useAdminServices } from '../state/admin-session';
import { ConnectionWizard } from './ConnectionWizard';
import { formatDate, sourceSummary, STATUS_LABEL, type VisibleStatus } from './connection-labels';
import { ErrorNotice, Notice } from './ErrorNotice';
import { PublishWizard } from './PublishWizard';

type Mode =
  | { readonly view: 'list' }
  | { readonly view: 'wizard'; readonly editing?: Connection }
  | { readonly view: 'publish'; readonly connection: Connection };

type Pending =
  | { readonly kind: 'delete-connection'; readonly connection: Connection }
  | { readonly kind: 'unpublish'; readonly modelId: string; readonly name: string };

interface Loaded {
  readonly catalog: ConnectorCatalog;
  readonly connections: readonly Connection[];
}

/**
 * Centro de conexiones: estado de cada fuente, alta y edicion guiadas, prueba, modelos
 * publicados y retirada. Cada accion la autoriza el servidor, no la interfaz.
 */
export function ConnectionsCenter() {
  const { admin } = useAdminServices();
  const uiDispatch = useUiDispatch();
  const [data, setData] = useState<Loaded | null>(null);
  const [loadError, setLoadError] = useState<AppError | null>(null);
  const [mode, setMode] = useState<Mode>({ view: 'list' });
  const [testing, setTesting] = useState<ReadonlySet<string>>(new Set());
  const [actionError, setActionError] = useState<AppError | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void Promise.all([admin.connectors(), admin.connections()]).then(([catalog, connections]) => {
      if (cancelled) return;
      if (!catalog.ok) setLoadError(catalog.error);
      else if (!connections.ok) setLoadError(connections.error);
      else {
        setLoadError(null);
        setData({ catalog: catalog.value, connections: connections.value });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [admin, revision]);

  const load = () => {
    setRevision((value) => value + 1);
  };

  const replace = (connection: Connection) => {
    setData((current) =>
      current === null
        ? current
        : {
            ...current,
            connections: current.connections.some((item) => item.id === connection.id)
              ? current.connections.map((item) => (item.id === connection.id ? connection : item))
              : [...current.connections, connection],
          },
    );
  };

  const test = async (connection: Connection) => {
    setTesting((current) => new Set(current).add(connection.id));
    setActionError(null);
    const result = await admin.testSaved(connection.id);
    setTesting((current) => {
      const next = new Set(current);
      next.delete(connection.id);
      return next;
    });
    if (result.ok) replace(result.value);
    else setActionError(result.error);
  };

  /** Retira los modelos de la conexion (si los hay) y despues la elimina. */
  const deleteConnection = async (connection: Connection) => {
    for (const model of connection.models) {
      const retired = await admin.unpublish(model.id);
      if (!retired.ok) return retired;
    }
    return admin.remove(connection.id);
  };

  const confirmPending = async () => {
    if (pending === null) return;
    setActionError(null);
    setConfirming(true);
    const result =
      pending.kind === 'delete-connection'
        ? await deleteConnection(pending.connection)
        : await admin.unpublish(pending.modelId);
    setConfirming(false);
    setPending(null);
    if (!result.ok) {
      setActionError(result.error);
      load();
      return;
    }
    setNotice(
      pending.kind === 'delete-connection'
        ? `Conexion "${pending.connection.name}" eliminada.`
        : 'Modelo retirado del analisis.',
    );
    load();
  };

  if (loadError) {
    return (
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-3 p-6">
        <ErrorNotice error={loadError} />
        <Button size="sm" variant="outline" className="self-start" onClick={load}>
          Reintentar
        </Button>
      </div>
    );
  }
  if (data === null) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner label="Cargando conexiones" />
      </div>
    );
  }

  if (mode.view === 'wizard') {
    return (
      <ConnectionWizard
        types={data.catalog.types}
        importEnabled={data.catalog.importEnabled}
        {...(mode.editing ? { editing: mode.editing } : {})}
        onCancel={() => {
          setMode({ view: 'list' });
        }}
        onSaved={(connection) => {
          replace(connection);
          setNotice(`Conexion "${connection.name}" guardada.`);
          setMode({ view: 'list' });
        }}
      />
    );
  }
  if (mode.view === 'publish') {
    return (
      <PublishWizard
        connection={mode.connection}
        onCancel={() => {
          setMode({ view: 'list' });
          load();
        }}
        onPublished={load}
        onOpenAnalysis={(modelId) => {
          uiDispatch({ type: 'open-analysis', modelId });
        }}
      />
    );
  }

  const typeLabel = (connectorId: string) =>
    data.catalog.types.find((type) => type.id === connectorId)?.label ?? connectorId;

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-4 p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Conexiones de datos</h2>
          <p className="text-sm text-muted-foreground">
            Conecte archivos o bases de datos, revise lo que contienen y publique modelos para el
            analisis.
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => {
            setNotice(null);
            setMode({ view: 'wizard' });
          }}
        >
          <Plus /> Nueva conexion
        </Button>
      </div>

      {notice && <Notice tone="info">{notice}</Notice>}
      {actionError && <ErrorNotice error={actionError} />}

      {data.connections.length === 0 ? (
        <Card className="p-0">
          <EmptyState
            icon={<Database />}
            title="Aun no hay conexiones"
            description="Empiece subiendo un archivo CSV, TXT o Parquet, o conectando PostgreSQL, SQL Server, MySQL o un SQL Warehouse de Databricks."
            action={
              <Button
                size="sm"
                onClick={() => {
                  setMode({ view: 'wizard' });
                }}
              >
                <Plus /> Crear la primera conexion
              </Button>
            }
          />
        </Card>
      ) : (
        <ul className="flex flex-col gap-3">
          {data.connections.map((connection) => {
            const status: VisibleStatus = testing.has(connection.id)
              ? 'connecting'
              : connection.status;
            const badge = STATUS_LABEL[status];
            return (
              <li key={connection.id}>
                <Card className="gap-3 p-4">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="truncate text-sm font-semibold">{connection.name}</h3>
                        <Badge variant={badge.variant}>{badge.label}</Badge>
                        {!connection.queryable && <Badge variant="warning">Solo exploracion</Badge>}
                      </div>
                      <p className="truncate text-xs text-muted-foreground">
                        {typeLabel(connection.connectorId)} · {sourceSummary(connection)}
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        Ultima prueba: {formatDate(connection.lastTestedAt)}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={testing.has(connection.id)}
                        onClick={() => void test(connection)}
                      >
                        <PlugZap /> Probar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setNotice(null);
                          setMode({ view: 'publish', connection });
                        }}
                      >
                        {connection.queryable ? <BarChart3 /> : <Search />}
                        {connection.queryable ? 'Crear modelo' : 'Explorar'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        aria-label={`Editar ${connection.name}`}
                        onClick={() => {
                          setNotice(null);
                          setMode({ view: 'wizard', editing: connection });
                        }}
                      >
                        <Pencil /> Editar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-destructive hover:text-destructive"
                        aria-label={`Eliminar ${connection.name}`}
                        onClick={() => {
                          setNotice(null);
                          setPending({ kind: 'delete-connection', connection });
                        }}
                      >
                        <Trash2 /> Eliminar
                      </Button>
                    </div>
                  </div>
                  {connection.status === 'error' && connection.message && (
                    <Notice tone="error">{connection.message}</Notice>
                  )}
                  {connection.warnings.map((warning) => (
                    <Notice key={warning} tone="warning">
                      {warning}
                    </Notice>
                  ))}
                  {connection.models.length > 0 && (
                    <div className="flex flex-col gap-1 border-t border-border pt-2">
                      <p className="text-xs font-medium">Modelos publicados</p>
                      <ul className="flex flex-col gap-1">
                        {connection.models.map((model) => (
                          <li
                            key={model.id}
                            className="flex flex-wrap items-center justify-between gap-2 text-sm"
                          >
                            <span>{model.name}</span>
                            <span className="flex gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  uiDispatch({ type: 'open-analysis', modelId: model.id });
                                }}
                              >
                                Abrir en analisis
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setPending({
                                    kind: 'unpublish',
                                    modelId: model.id,
                                    name: model.name,
                                  });
                                }}
                              >
                                Retirar
                              </Button>
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </Card>
              </li>
            );
          })}
        </ul>
      )}

      <ConfirmDialog
        pending={pending}
        busy={confirming}
        onCancel={() => {
          if (!confirming) setPending(null);
        }}
        onConfirm={() => void confirmPending()}
      />
    </div>
  );
}

function ConfirmDialog({
  pending,
  busy,
  onCancel,
  onConfirm,
}: {
  readonly pending: Pending | null;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}) {
  const models = pending?.kind === 'delete-connection' ? pending.connection.models : [];
  const title =
    pending?.kind === 'delete-connection'
      ? `Eliminar la conexion "${pending.connection.name}"`
      : pending?.kind === 'unpublish'
        ? `Retirar el modelo "${pending.name}"`
        : '';
  const description =
    pending?.kind === 'delete-connection'
      ? 'Se borraran la conexion, su archivo copiado (si lo tiene) y la contrasena guardada. Esta accion no se puede deshacer.'
      : 'El modelo dejara de estar disponible en Analisis. Los borradores de analisis que lo usen no podran abrirse hasta publicarlo de nuevo.';
  const confirmLabel =
    pending?.kind === 'unpublish'
      ? 'Retirar'
      : models.length > 0
        ? 'Retirar modelos y eliminar'
        : 'Eliminar';
  return (
    <Dialog
      open={pending !== null}
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
    >
      <DialogContent>
        <div className="flex flex-col gap-2 pr-6">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
          {models.length > 0 && (
            <Notice tone="warning">
              Esta conexion alimenta{' '}
              {models.length === 1 ? 'el modelo publicado' : 'los modelos publicados'}{' '}
              {models.map((model) => `"${model.name}"`).join(', ')}. Tambien se retiraran del
              analisis.
            </Notice>
          )}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button size="sm" variant="outline" disabled={busy} onClick={onCancel}>
            Cancelar
          </Button>
          <Button size="sm" variant="destructive" disabled={busy} onClick={onConfirm}>
            {busy ? 'Procesando...' : confirmLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
